import { useRouter } from 'next/router'
import { useTranslations } from 'next-intl'

import type { SanityClient } from '@sanity/client'

import { indexes } from '@mab-common/utils'

import ContainerDivider from '~/components/atoms/ContainerDivider/ContainerDivider'
import PageHeader from '~/components/atoms/PageHeader/PageHeader'
import { TextSizes } from '~/components/atoms/Text/Text'
import VerticalPadder from '~/components/atoms/VerticalPadder/VerticalPadder'
import type { AdvantagesBannerProps } from '~/components/blocks/AdvantagesBanner/AdvantagesBanner'
import AdvantagesBanner from '~/components/blocks/AdvantagesBanner/AdvantagesBanner'
import ArtistProfileList from '~/components/blocks/ArtistProfileList/ArtistProfileList'
import GenreSlider from '~/components/blocks/GenreSlider/GenreSlider'
import { useSettingsContext } from '~/contexts/settingsContext'
import { FeatureFlags } from '~/data/posthog/featureFlags'
import { BannerTheme } from '~/enums/banner-theme'
import { useFeatureFlag } from '~/hooks/useFeatureFlag'
import { advantagesBanner, internationalizedTextField } from '~/schemas/common'
import {
  SearchAnalystEventNames,
  SearchAnalystMethods,
} from '~/services/elastic/analyst'
import { PushInteractionLocation } from '~/utils/posthogEvents'

import type { PageTemplateModule, TemplateParams } from './types'

import RecordTypes = indexes.RecordTypes
import ArtworksList from '~/components/blocks/ArtworksList/ArtworksList'

const artworksListEvents = {
  artwork: {
    method: SearchAnalystMethods.clickedObjectIDsAfterSearch,
    name: SearchAnalystEventNames.artworksArtworkClicked,
  },
  artist: {
    method: SearchAnalystMethods.clickedFilters,
    name: SearchAnalystEventNames.artworksArtworkArtistClicked,
  },
  filter: {
    method: SearchAnalystMethods.clickedFilters,
    name: SearchAnalystEventNames.artworksArtworkFilterClicked,
  },
}

interface TradingFloorTemplateData {
  pageSettings: {
    advantagesBanner: AdvantagesBannerProps
    tradingPageSummary: string
  }
}

/**
 * Trading Floor Template Component
 * Renders the trading floor page with featured artworks
 */
function TradingFloorTemplate({ data }: { data: any }) {
  const { pageSettings, breadcrumb } = data
  const router = useRouter()
  const tNavigation = useTranslations('navigation')
  const t = useTranslations()
  const tPage = useTranslations('tradingFloorPage')
  const { getFlagByName } = useFeatureFlag()
  const tradingFloorFlag = getFlagByName(FeatureFlags.tradingFloorNew)

  const { genres, featuredArtists, totalArtists } = useSettingsContext()

  // Override breadcrumb at runtime based on feature flag
  const displayBreadcrumb =
    tradingFloorFlag !== 'control'
      ? breadcrumb.map((item: any, index: number) =>
          index === breadcrumb.length - 1 && item.title === 'trading floor'
            ? { ...item, title: tPage('whatsNew') }
            : item
        )
      : breadcrumb

  return (
    <>
      <PageHeader
        title={
          tradingFloorFlag !== 'control'
            ? tPage('whatsNew')
            : tNavigation.rich('tradingFloor', {
                em: chunks => <em>{chunks}</em>,
              })
        }
        summary={pageSettings.tradingPageSummary}
        preventSplitLines
      />

      <div id="tradingContainer">
        {/* <LiveMarketList
          pageSize={12}
          sectionId="buy"
          searchPlaceholder={t('common.searchArtistOrArtworkTitle')}
          searchInsightsEvents={artworksListEvents}
          hideFilters={['genre', 'format']}
          controlsTopZero
          initialFilters={
            tradingFloorFlag !== 'control'
              ? { tradingFloor: 'sell' }
              : undefined
          }
        /> */}
        <ArtworksList
          pageSize={12}
          sectionId="buy"
          searchPlaceholder={t('common.searchArtistOrArtworkTitle')}
          searchInsightsEvents={artworksListEvents}
          hideFilters={['genre', 'format']}
          controlsTopZero
          initialFilters={
            tradingFloorFlag !== 'control'
              ? { tradingFloor: 'sell' }
              : undefined
          }
        />
      </div>

      <ArtistProfileList
        artists={featuredArtists ? featuredArtists : []}
        totalArtists={totalArtists ? totalArtists : 0}
        totalArtistsTitle={tPage('otherNotableArtists')}
        allBtnHandler={() => router.push('/artists')}
        titleSize={TextSizes.primaryTitleMed}
        title={tPage('featuredArtists')}
        recordType={RecordTypes.ARTIST}
        searchInsightsEvent={{
          method: SearchAnalystMethods.clickedFilters,
          name: SearchAnalystEventNames.artworksArtistClicked,
        }}
        ariaControlId="artist-list"
        showViewAll={true}
      />

      {genres && (
        <>
          <ContainerDivider />
          <VerticalPadder>
            <GenreSlider
              genres={genres}
              searchInsightsEvent={{
                method: SearchAnalystMethods.clickedFilters,
                name: SearchAnalystEventNames.homeGenreClicked,
              }}
              title={tPage('discover')}
            />
          </VerticalPadder>
        </>
      )}

      <div id="sell-container">
        <AdvantagesBanner
          {...pageSettings.advantagesBanner}
          theme={BannerTheme.offwhite}
          analyticsInteractionLocation={
            PushInteractionLocation.TradingFloorPage
          }
          analyticsCtaSource="TradingFloorPage"
        />
      </div>
    </>
  )
}

/**
 * Trading Floor Page Template Module
 * Fetches settings data with advantagesBanner and tradingPageSummary
 */
export const tradingFloorTemplate: PageTemplateModule<TradingFloorTemplateData> =
  {
    async fetchData(client: SanityClient, params: TemplateParams) {
      const { lang } = params

      const data = await client.fetch<TradingFloorTemplateData>(
        `{
        "pageSettings": *[_type == 'settings'][0] {
          ${advantagesBanner},
          "tradingPageSummary": ${internationalizedTextField('tradingPageSummary')}
        }
      }`,
        { lang }
      )

      return data
    },

    component: TradingFloorTemplate,
  }
